import type { ApiResponse } from "@entrilia/shared/api/types";
import { ReportAccessType } from "@entrilia/shared/api/reportAccessTypes";
import biApi from "../../../../../api/biApi";
import type {
  ApiReportResponse,
  BuildExportRequest,
  CancellationToken,
  ExportResponse,
  Report,
  ReportAuthorizationInfo,
  RunExportRequest,
  RunTabularReportRequest,
  TabularDataResponse,
  TabularReportConfigurationForRunning,
} from "../../../../../api/types/svTypes";
import type { BiApiClient } from "../../sv/contexts/ApiClientProviderContext";
import { removeErrorCode } from "../canvas/runErrorMessage";
import type { SourceReportRequest } from "./sourceReportRequest";
import { toRunSourceRequest } from "./sourceReportRequest";

const REPORT_NOT_SAVED_MESSAGE = "Changes in this window are not saved.";
const ACTION_UNAVAILABLE_MESSAGE = "This action is not available in this window.";

export interface ArtifactReportApi {
  getSourceReport: (mode: "run" | "edit") => Promise<ApiResponse<Report>>;
  getSourceReportRunConfiguration: () => Promise<ApiResponse<TabularReportConfigurationForRunning>>;
  runSourceReport: <TBody extends RunTabularReportRequest, TResponse extends TabularDataResponse | ApiReportResponse>(
    body: TBody,
    done: (data: ApiResponse<TResponse>) => void,
    error: (reason: string | undefined) => void
  ) => CancellationToken;
  requestSourceReportExport: (body: RunExportRequest) => Promise<ApiResponse<ExportResponse>>;
  buildTabularReport: BiApiClient["buildTabularReport"];
  requestBuildTabularExport: (body: BuildExportRequest) => Promise<ApiResponse<ExportResponse>>;
}

const readOnlyAuthorization: ReportAuthorizationInfo = {
  private: true,
  own: false,
  access: ReportAccessType.Read,
  sharedWith: [],
};

const failWith =
  (message: string) =>
  <TData>(): Promise<ApiResponse<TData>> =>
    Promise.resolve({ success: false, error: { message } });

const refuseWrite = failWith(REPORT_NOT_SAVED_MESSAGE);
const refuseRead = failWith(ACTION_UNAVAILABLE_MESSAGE);

export const createArtifactReportApiClient = (api: ArtifactReportApi): BiApiClient => ({
  getReport: async (reportId, mode) => {
    const response = await api.getSourceReport(mode);
    return response.success ? { success: true, data: { ...response.data, reportId } } : response;
  },
  getReportAuthorizationInfo: () => Promise.resolve({ success: true, data: readOnlyAuthorization }),
  getReportConfigurationForRunning: () => api.getSourceReportRunConfiguration(),
  runTabularReport: (_dataSetId, body, done, error) =>
    api.runSourceReport(body, done, (reason) => error(reason === undefined ? undefined : removeErrorCode(reason))),
  requestRunTabularExport: (body) => api.requestSourceReportExport(body),
  buildTabularReport: (body, done, error) => api.buildTabularReport({ ...body, reportId: "" }, done, error),
  requestBuildTabularExport: (body) =>
    api.requestBuildTabularExport({ ...body, buildConfig: { ...body.buildConfig, reportId: "" } }),
  updateReport: refuseWrite,
  addReport: refuseWrite,
  createDraftReport: refuseWrite,
  renameReport: refuseWrite,
  changeReportGroup: refuseWrite,
  deleteReports: refuseWrite,
  updateReportSharing: refuseWrite,
  getReportHistory: refuseRead,
  getReports: refuseRead,
  getReportSharing: refuseRead,
  validateName: refuseRead,
  getEditReportUrl: () => "",
  getViewReportUrl: () => "",
  getReportsUrl: () => "",
});

const requestBuildTabularExport = (body: BuildExportRequest): Promise<ApiResponse<ExportResponse>> =>
  biApi.uiReports.requestBuildTabularExport?.(body) ??
  Promise.resolve({ success: false, error: { message: ACTION_UNAVAILABLE_MESSAGE } });

export const bindArtifactReportApi = (artifactId: string, request: SourceReportRequest): ArtifactReportApi => ({
  getSourceReport: (mode) =>
    biApi.smartViewArtifacts.getSourceReport(
      artifactId,
      request.version,
      request.sourceKey,
      mode,
      toRunSourceRequest(request)
    ),
  getSourceReportRunConfiguration: () =>
    biApi.smartViewArtifacts.getSourceReportRunConfiguration(
      artifactId,
      request.version,
      request.sourceKey,
      toRunSourceRequest(request)
    ),
  runSourceReport: (body, done, error) =>
    biApi.smartViewArtifacts.runSourceReport(artifactId, request.version, request.sourceKey, body, done, error),
  requestSourceReportExport: (body) =>
    biApi.smartViewArtifacts.requestSourceReportExport(artifactId, request.version, request.sourceKey, body),
  buildTabularReport: biApi.uiReports.buildTabularReport,
  requestBuildTabularExport,
});
